import { Button, HStack, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text, VStack } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { MdOutlineWebhook } from 'react-icons/md';
import { useAddJobWebhookConfigMutation, useModalitiesQuery } from '@/app/services/nucleus';
import LoaderOverlay from '../common/LoaderOverlay';
import AlertComponent from '../common/AlertComponent';
import JobFeedFormFields from './JobFeedFormFields';
import { isValidHttpsUrl, jobFeedErrorMessage, modalitiesToOptions } from './jobFeedHelpers';

const JobFeedModal = ({ isOpen, onClose, onSave }) => {
  const initialRef = useRef(null);
  const finalRef = useRef(null);

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [modalities, setModalities] = useState([]);
  const [nameError, setNameError] = useState(false);
  const [urlError, setUrlError] = useState(false);

  const { data: modalitiesData } = useModalitiesQuery();
  const [addConfig, { isLoading: isSubmitting, isError, error, reset }] = useAddJobWebhookConfigMutation();

  const modalityOptions = useMemo(() => modalitiesToOptions(modalitiesData?.items || []), [modalitiesData?.items]);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setUrl('');
      setModalities([]);
      setNameError(false);
      setUrlError(false);
      reset();
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    let hasError = false;
    if (!name.trim()) {
      setNameError(true);
      hasError = true;
    }
    if (!isValidHttpsUrl(url)) {
      setUrlError(true);
      hasError = true;
    }
    if (hasError) return;
    reset();

    addConfig({
      name: name.trim(),
      url: url.trim(),
      modality_ids: modalities.map((m) => m.value),
    })
      .unwrap()
      .then(() => {
        onSave();
        onClose();
      })
      .catch((err) => console.error('Error adding job feed config', err));
  };

  return (
    <Modal initialFocusRef={initialRef} finalFocusRef={finalRef} isOpen={isOpen} onClose={onClose} isCentered size="2xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <form id="add_job_feed" onSubmit={handleSubmit}>
          <ModalHeader>
            <HStack>
              <MdOutlineWebhook />
              <Text>Add Job Feed</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} position="relative">
              <LoaderOverlay loading={isSubmitting} />
              {isError && <AlertComponent status="error" title="Error adding job feed" description={jobFeedErrorMessage(error)} />}
              <JobFeedFormFields
                initialRef={initialRef}
                name={name}
                onNameChange={(v) => {
                  setName(v);
                  setNameError(false);
                }}
                nameError={nameError}
                url={url}
                onUrlChange={(v) => {
                  setUrl(v);
                  setUrlError(false);
                }}
                urlError={urlError}
                modalities={modalities}
                onModalitiesChange={setModalities}
                modalityOptions={modalityOptions}
              />
              <HStack width="100%" py={3} justifyContent="end">
                <Button onClick={onClose}>Cancel</Button>
                <Button type="submit" variant="purple">
                  Submit
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </form>
      </ModalContent>
    </Modal>
  );
};

JobFeedModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
};

export default JobFeedModal;
